import { useEffect, useState } from "react";
import { ApiKeyGate } from "./components/ApiKeyGate";
import { ChatView } from "./components/chat/ChatView";
import { LibraryView } from "./components/artifacts/LibraryView";
import { SkillsView } from "./components/skills/SkillsView";
import { ToolsView } from "./components/tools/ToolsView";
import { MemoryView } from "./components/memory/MemoryView";
import { Sidebar } from "./components/chat/Sidebar";
import { useStore } from "./lib/store";
import { inTauri } from "./lib/tools";
import "./lib/workspace-tools";
import "./lib/sandbox-tools";
import { ApprovalDialog } from "./components/workspace/ApprovalDialog";
import "./index.css";

type Route =
  | { kind: "chat" }
  | { kind: "library"; selectedId: string | null }
  | { kind: "skills"; selectedId: string | null }
  | { kind: "tools" }
  | { kind: "memory" };

function matchRoute(): Route {
  if (typeof window === "undefined") return { kind: "chat" };
  const lib = window.location.pathname.match(/^\/library(?:\/(.+))?$/);
  if (lib) return { kind: "library", selectedId: lib[1] ?? null };
  const sk = window.location.pathname.match(/^\/skills(?:\/(.+))?$/);
  if (sk) return { kind: "skills", selectedId: sk[1] ?? null };
  if (window.location.pathname === "/tools") return { kind: "tools" };
  if (window.location.pathname === "/memory") return { kind: "memory" };
  return { kind: "chat" };
}

export function App() {
  const [route, setRoute] = useState<Route>(matchRoute);
  const sidebarOpen = useStore((s) => s.sidebarOpen);
  const setSidebarOpen = useStore((s) => s.setSidebarOpen);
  const refreshActiveRuns = useStore((s) => s.refreshActiveRuns);

  useEffect(() => {
    const onPop = () => setRoute(matchRoute());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("sidebar-open", sidebarOpen);
    return () => { document.body.classList.remove("sidebar-open"); };
  }, [sidebarOpen]);

  useEffect(() => {
    if (!sidebarOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSidebarOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sidebarOpen, setSidebarOpen]);

  useEffect(() => {
    if (inTauri) document.body.classList.add("tauri-titlebar");
  }, []);

  useEffect(() => {
    if (!inTauri) return;
    const onDblClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target?.closest("[data-tauri-drag-region]")) return;
      if (target.closest("button, a, input, textarea, select")) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      void import("@tauri-apps/api/window").then(({ getCurrentWindow }) =>
        getCurrentWindow().minimize(),
      ).catch((err) => console.error("minimize failed:", err));
    };
    window.addEventListener("dblclick", onDblClick, true);
    return () => window.removeEventListener("dblclick", onDblClick, true);
  }, []);

  useEffect(() => {
    void refreshActiveRuns();
    const id = setInterval(() => { void refreshActiveRuns(); }, 4000);
    return () => clearInterval(id);
  }, [refreshActiveRuns]);

  const exitOverlay = () => {
    if (window.location.pathname !== "/") {
      history.back();
    }
    setRoute({ kind: "chat" });
  };

  return (
    <ApiKeyGate>
      {inTauri && (
        <div
          data-tauri-drag-region
          className="fixed top-0 left-0 right-0 z-[60] pointer-events-auto"
          style={{ height: "var(--titlebar-h, 28px)" }}
        />
      )}
      {sidebarOpen && (
        <>
          <div
            onClick={() => setSidebarOpen(false)}
            className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          />
          <div className="fixed left-0 inset-y-0 z-50 w-[80vw] sm:w-72 sidebar-vt">
            <Sidebar />
          </div>
        </>
      )}
      <ChatView />
      {route.kind === "library" && <LibraryView initialSelectedId={route.selectedId} onExit={exitOverlay} />}
      {route.kind === "skills" && <SkillsView initialSelectedId={route.selectedId} onExit={exitOverlay} />}
      {route.kind === "tools" && <ToolsView onExit={exitOverlay} />}
      {route.kind === "memory" && <MemoryView onExit={exitOverlay} />}
      <ApprovalDialog />
    </ApiKeyGate>
  );
}

export default App;
